import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ClipboardList, FileText, Search, Filter, Clock, CheckCircle2, ArrowRight,
  Loader2, Inbox, Sparkles, GitCompare, X, Check,
} from "lucide-react";
import { TopBar } from "@/components/PageShell";
import { CourtLabel } from "@/components/CourtLabel";
import { sanitizeDjenText } from "@/lib/djenText";

const NAVY = "#0E1D2D";
const GOLD = "#C9953D";

const fmtDate = (s) => s
  ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
  : "—";

const STATUS_META = {
  AGUARDANDO_REVISAO: { label: "Aguardando revisão", tone: "amber", icon: Clock },
  EM_REVISAO: { label: "Em revisão", tone: "blue", icon: Clock },
  REVISADO: { label: "Revisado", tone: "emerald", icon: CheckCircle2 },
  PROTOCOLADO: { label: "Protocolado", tone: "slate", icon: CheckCircle2 },
};

const TONE = {
  amber: "bg-amber-100 text-amber-900 border-amber-200",
  blue: "bg-blue-100 text-blue-900 border-blue-200",
  emerald: "bg-emerald-100 text-emerald-900 border-emerald-200",
  slate: "bg-slate-100 text-slate-800 border-slate-200",
};

const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "AGUARDANDO_REVISAO", label: "Aguardando" },
  { key: "EM_REVISAO", label: "Em revisão" },
  { key: "REVISADO", label: "Revisados" },
  { key: "PROTOCOLADO", label: "Protocolados" },
];

export default function PreparadosList() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);   // array of ids, max 2

  // Pré-seleção via ?compareFrom=<id> (originada em /preparado/:id)
  useEffect(() => {
    const from = params.get("compareFrom");
    if (from) {
      setSelectMode(true);
      setSelected([from]);
      toast.info("Selecione a segunda peça para comparar");
    }
  }, [params]);

  useEffect(() => {
    let stop = false;
    api.get("/prepared-documents", { params: { limit: 200 } })
      .then((r) => { if (!stop) setItems(r.data.items || []); })
      .catch(() => toast.error("Falha ao carregar preparados"))
      .finally(() => !stop && setLoading(false));
    return () => { stop = true; };
  }, []);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 2) {
        toast.info("Máximo de 2 preparados por comparação");
        return prev;
      }
      return [...prev, id];
    });
  };

  const clearSelection = () => { setSelected([]); setSelectMode(false); };

  const goCompare = () => {
    if (selected.length !== 2) {
      toast.error("Selecione exatamente 2 preparados");
      return;
    }
    navigate(`/preparados/comparar?a=${selected[0]}&b=${selected[1]}`);
  };

  const filteredHermes = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((d) => {
      if (d.origin !== "DJEN") return false;
      if (status !== "all" && d.status !== status) return false;
      if (!term) return true;
      const hay = `${d.title || ""} ${d.document_type || ""} ${d.process_number || ""} ${d.orgao || ""} ${d.tribunal || ""}`.toLowerCase();
      return hay.includes(term);
    });
  }, [items, q, status]);

  const filteredMeus = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((d) => {
      if (d.origin === "DJEN") return false;
      if (status !== "all" && d.status !== status) return false;
      if (!term) return true;
      const hay = `${d.title || ""} ${d.document_type || ""} ${d.process_number || ""} ${d.orgao || ""} ${d.tribunal || ""}`.toLowerCase();
      return hay.includes(term);
    });
  }, [items, q, status]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((d) => {
      if (status !== "all" && d.status !== status) return false;
      if (!term) return true;
      const hay = `${d.title || ""} ${d.document_type || ""} ${d.process_number || ""} ${d.orgao || ""} ${d.tribunal || ""}`.toLowerCase();
      return hay.includes(term);
    });
  }, [items, q, status]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const key of Object.keys(STATUS_META)) c[key] = 0;
    items.forEach((d) => { if (c[d.status] != null) c[d.status] += 1; });
    return c;
  }, [items]);

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="preparados-list-page">
      <TopBar />
      <div className="flex-1 p-3 sm:p-6 lg:p-8 font-sans">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: GOLD }}>
              <ClipboardList className="w-3 h-3 inline mr-1"/> Preparados Hermes
            </div>
            <h1 className="font-heading text-3xl mt-1" style={{ color: NAVY }}>
              Documentos preparados
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Rascunhos gerados a partir de publicações DJEN, prazos e ações de IA. Revise antes de protocolar.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setSelectMode(m => !m); setSelected([]); }}
              data-testid="preparados-compare-toggle"
              className={`inline-flex items-center gap-2 h-10 px-4 rounded-lg text-sm font-semibold border transition ${
                selectMode
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}>
              <GitCompare className="w-4 h-4"/> {selectMode ? "Cancelar seleção" : "Comparar peças"}
            </button>
            <Link to="/djen" data-testid="preparados-cta-djen"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg text-white text-sm font-semibold shadow-sm hover:opacity-95"
              style={{ background: NAVY }}>
              <Sparkles className="w-4 h-4"/> Ir ao DJEN
            </Link>
          </div>
        </div>

        {/* Filtros de status */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          <Filter className="w-4 h-4 text-slate-400"/>
          {FILTERS.map((f) => {
            const active = status === f.key;
            const count = counts[f.key] ?? 0;
            return (
              <button key={f.key} onClick={() => setStatus(f.key)}
                data-testid={`preparados-filter-${f.key.toLowerCase()}`}
                className={`h-8 px-3 rounded-full text-[12px] font-semibold border inline-flex items-center gap-1.5 transition ${
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                }`}>
                {f.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
                }`}>{count}</span>
              </button>
            );
          })}
          <div className="ml-auto relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por título, processo, órgão…"
              data-testid="preparados-search"
              className="h-9 w-72 max-w-full pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#c9953d]/40 focus:border-[#c9953d]"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400"/></div>
        ) : filtered.length === 0 && !selectMode ? (
          <EmptyState hasItems={items.length > 0} />
        ) : selectMode ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pb-24">
            {filtered.map((d) => (
              <PreparadoCard key={d.id} doc={d}
                selectMode={selectMode}
                selected={selected.includes(d.id)}
                onToggleSelect={() => toggleSelect(d.id)}/>
            ))}
          </div>
        ) : (
          <TwoColumnPreparados
            filteredHermes={filteredHermes}
            filteredMeus={filteredMeus}
            totalItems={items.length}
          />
        )}

        {/* Barra flutuante de comparação */}
        {selectMode && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 shadow-2xl rounded-2xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3"
               data-testid="preparados-compare-bar">
            <div className="text-sm">
              <span className="font-semibold text-slate-900">{selected.length}/2</span>
              <span className="text-slate-500 ml-1">selecionado{selected.length !== 1 ? "s" : ""}</span>
            </div>
            <button onClick={clearSelection}
              className="h-8 px-3 text-[12.5px] rounded-lg text-slate-600 hover:bg-slate-100 inline-flex items-center gap-1"
              data-testid="preparados-compare-clear">
              <X className="w-3.5 h-3.5"/> Limpar
            </button>
            <button onClick={goCompare}
              disabled={selected.length !== 2}
              data-testid="preparados-compare-go"
              className="h-9 px-4 rounded-lg text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-95"
              style={{ background: GOLD }}>
              <GitCompare className="w-4 h-4"/> Comparar agora
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function TwoColumnPreparados({ filteredHermes, filteredMeus, totalItems }) {
  const [mobileTab, setMobileTab] = useState("hermes");

  const HermesCol = (
    <ColumnPanel
      testid="preparados-col-hermes"
      badgeText="HERMES"
      badgeTone="gold"
      title="Preparados por Hermes"
      subtitle="Documentos e providências preparados automaticamente a partir do DJEN."
      items={filteredHermes}
      empty={{
        title: "Nenhum preparado automático no momento.",
        subtitle: "Quando o Hermes preparar uma providência a partir do DJEN, ela aparecerá aqui.",
        cta: { label: "Abrir DJEN", to: "/djen" },
      }}
    />
  );
  const MeusCol = (
    <ColumnPanel
      testid="preparados-col-meus"
      badgeText="MEU PREPARADO"
      badgeTone="navy"
      title="Meus Preparados"
      subtitle="Documentos preparados por você com auxílio do Hermes IA."
      items={filteredMeus}
      empty={{
        title: "Você ainda não criou nenhum preparado.",
        subtitle: "Use \"Preparar com Hermes IA\" a partir de qualquer template salvo.",
        cta: { label: "Ver modelos IA", to: "/templates?tab=ia" },
      }}
    />
  );

  return (
    <div className="pb-8" data-testid="preparados-twocolumn">
      <div className="md:hidden mb-3 flex gap-1 p-1 bg-slate-100 rounded-lg">
        <button onClick={() => setMobileTab("hermes")} data-testid="preparados-mtab-hermes"
          className={`flex-1 h-9 rounded-md text-[12px] font-semibold ${mobileTab === "hermes" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}>
          Por Hermes ({filteredHermes.length})
        </button>
        <button onClick={() => setMobileTab("meus")} data-testid="preparados-mtab-meus"
          className={`flex-1 h-9 rounded-md text-[12px] font-semibold ${mobileTab === "meus" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}>
          Meus ({filteredMeus.length})
        </button>
      </div>
      <div className="md:hidden">{mobileTab === "hermes" ? HermesCol : MeusCol}</div>
      <div className="hidden md:grid md:grid-cols-2 gap-4">{HermesCol}{MeusCol}</div>
    </div>
  );
}

function ColumnPanel({ testid, badgeText, badgeTone, title, subtitle, items, empty }) {
  const badgeCls = badgeTone === "gold"
    ? "bg-amber-100 text-amber-900 border border-amber-200"
    : "bg-slate-900 text-white";
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-1 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${badgeCls}`}>{badgeText}</span>
          <h2 className="font-heading text-lg font-semibold truncate" style={{ color: NAVY }}>{title}</h2>
        </div>
        <span className="text-[11px] text-slate-500 shrink-0">{items.length}</span>
      </div>
      <p className="text-xs text-slate-500 mb-3">{subtitle}</p>
      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center">
          <Inbox className="w-8 h-8 mx-auto text-slate-300 mb-2"/>
          <div className="text-sm font-semibold text-slate-800">{empty.title}</div>
          <p className="text-xs text-slate-500 mt-1">{empty.subtitle}</p>
          {empty.cta && (
            <Link to={empty.cta.to} className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-white text-[12px] font-semibold hover:opacity-95" style={{ background: NAVY }}>
              <Sparkles className="w-3 h-3"/> {empty.cta.label}
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-2.5 max-h-[65vh] overflow-y-auto pr-1">
          {items.map((d) => <PreparadoCard key={d.id} doc={d}/>)}
        </div>
      )}
    </div>
  );
}

function PreparadoCard({ doc, selectMode = false, selected = false, onToggleSelect }) {
  const meta = STATUS_META[doc.status] || STATUS_META.AGUARDANDO_REVISAO;
  const StatusIcon = meta.icon;

  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            {selectMode && (
              <span
                className={`w-4 h-4 rounded border inline-flex items-center justify-center shrink-0 ${
                  selected
                    ? "bg-[#C9953D] border-[#C9953D] text-white"
                    : "bg-white border-slate-300"
                }`}
                data-testid={`preparado-select-${doc.id}`}
                aria-checked={selected}
                role="checkbox">
                {selected && <Check className="w-2.5 h-2.5" strokeWidth={3}/>}
              </span>
            )}
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${TONE[meta.tone]}`}>
              <StatusIcon className="w-3 h-3"/> {meta.label}
            </span>
            {doc.tribunal && <CourtLabel raw={doc.tribunal} size="xs" variant="chip"/>}
            {doc.origin && (
              <span className="text-[10px] uppercase tracking-widest text-slate-500">
                {doc.origin}
              </span>
            )}
          </div>
          <div className="font-semibold text-slate-900 truncate" style={{ color: NAVY }}>
            <FileText className="w-4 h-4 inline mr-1.5 text-slate-400"/>
            {sanitizeDjenText(doc.title || doc.document_type || "Preparado")}
          </div>
          {doc.process_number && (
            <div className="mt-1 font-mono text-[12px] text-slate-700">{doc.process_number}</div>
          )}
          {doc.orgao && (
            <div className="text-[11px] text-slate-500 truncate">{sanitizeDjenText(doc.orgao)}</div>
          )}
        </div>
        {!selectMode && (
          <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition mt-1 shrink-0"/>
        )}
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
        <span>Criado {fmtDate(doc.created_at)}</span>
        {doc.updated_at && <span>Atualizado {fmtDate(doc.updated_at)}</span>}
      </div>
    </>
  );

  const baseCls = `bg-white rounded-xl border p-4 transition group ${
    selected
      ? "border-[#C9953D] ring-2 ring-[#C9953D]/30 shadow-md"
      : "border-slate-200 hover:shadow-md hover:border-slate-300"
  }`;

  if (selectMode) {
    return (
      <button type="button"
        onClick={onToggleSelect}
        data-testid={`preparado-card-${doc.id}`}
        className={`${baseCls} text-left w-full cursor-pointer`}
        aria-pressed={selected}>
        {inner}
      </button>
    );
  }

  return (
    <Link to={`/preparado/${doc.id}`} data-testid={`preparado-card-${doc.id}`} className={baseCls}>
      {inner}
    </Link>
  );
}

function EmptyState({ hasItems }) {
  return (
    <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center" data-testid="preparados-empty">
      <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3"/>
      <div className="text-slate-800 font-semibold">
        {hasItems ? "Nenhum preparado com esse filtro" : "Nenhum preparado ainda"}
      </div>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        {hasItems
          ? "Ajuste os filtros de status ou limpe a busca."
          : "Ao acionar \"Cumprir Prazo\" em uma publicação DJEN, o Hermes gera um rascunho revisável aqui."}
      </p>
      {!hasItems && (
        <Link to="/djen" data-testid="preparados-empty-cta"
          className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-lg text-white text-sm font-semibold shadow-sm hover:opacity-95"
          style={{ background: NAVY }}>
          <Sparkles className="w-4 h-4"/> Abrir publicações DJEN
        </Link>
      )}
    </div>
  );
}
